import React, { useEffect, useState } from "react";
import { FaRegFilePdf } from "react-icons/fa6";
import { TbFileExcel } from "react-icons/tb";
import { FaRegCopy } from "react-icons/fa";
import { BsFiletypeCsv } from "react-icons/bs";
import { useAssetContext } from "../../DataContext/AssetContext";
import { exportTableToPDF, useExportLogo, exportTableToExcel } from "../ConfigCharts/ExcelCurved";
import { exportToCSV } from "../ConfigCharts/ExportUtils";
import WindmillLoading from "../../Loading/Loading";
import ApiErrorMessage from "./ApiErrorMessage"; 
import { DateTime } from "luxon";


interface VolumeTableProps {
  visibility: Record<string, boolean>;
  setVisibility: (visibility: Record<string, boolean>) => void;
  title?: string;
  refreshCounter?: number;
}

const TableComponent: React.FC<VolumeTableProps> = ({ visibility, title,refreshCounter }) => {
  const {
    volumeAsset,
    volumeMetrics,
    selectedResource,
    setSelectedResource,
    selectedFrequency,
    useIntervalAggregation,
    isvolumeLoading,
    apiError,
    selectedDateRange,
    metricConfigData,
    volumeData,
  } = useAssetContext();

  const allVolumeMetrics: Record<string, string[]> = (metricConfigData || []).reduce(
    (acc, group) => {
      if (group.group_key && (group.metric_list || []).length > 0) {
        acc[group.group_key] = group.metric_list;
      }
      return acc;
    },
    {} as Record<string, string[]>
  );

  interface Resource {
    RESOURCE_ID: string;
    RESOURCE_NAME: string;
    [metric: string]: any;
  }

  interface ChartData {
    time: string;
    RESOURCE_NAME?: string;
    [key: string]: string | number | undefined;
  }

  const [tableData, setTableData] = useState<ChartData[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const { logo } = useExportLogo();

  useEffect(() => {
    if (!selectedResource) {
      setSelectedResource("ALL");
    }
  }, [selectedResource, setSelectedResource,refreshCounter]);

  useEffect(() => {
    if (isvolumeLoading) {
      setTableData([]);
      return;
    }
    const allAssetData: Resource[] = Array.isArray(volumeAsset?.RESOURCES)
      ? volumeAsset.RESOURCES
      : [];

    const allData =
      selectedResource === "ALL"
        ? volumeAsset
          ? [volumeAsset]
          : []
        : allAssetData.filter((resource) => resource.RESOURCE_NAME === selectedResource);

    const rawChartData: ChartData[] = allData.flatMap((resource: Resource) =>
      Object.entries(resource)
        .filter(
          ([key, values]) =>
            key !== "RESOURCE_ID" &&
            key !== "RESOURCE_NAME" &&
            typeof values === "object" &&
            values !== null &&
            !Array.isArray(values)
        )
        .flatMap(([metric, values]) =>
          Object.entries(values).map(([time, value]) => ({
            time,
            RESOURCE_NAME: resource.RESOURCE_NAME,
            [metric]: value !== null && value !== undefined ? Number(value) : undefined,
          }))
        )
    );

    const effectiveMetrics: string[] = volumeMetrics.length > 0
      ? volumeMetrics
      : Array.from(new Set(
          rawChartData.flatMap(entry =>
            Object.keys(entry).filter(k => k !== "time" && k !== "RESOURCE_NAME")
          )
        ));

    const groupedData: { [key: string]: ChartData } = {};

    rawChartData.forEach((entry) => {
      if (!entry.time || entry.time === "0") return;

      const hourKey = new Date(entry.time).getTime();
      if (isNaN(hourKey)) return;

      if (!groupedData[hourKey]) {
        groupedData[hourKey] = { time: entry.time };
      }

      effectiveMetrics.forEach((metric) => {
        const value = entry[metric];
        if (typeof value === "number" && !isNaN(value)) {
          if (groupedData[hourKey][metric] === undefined) {
            groupedData[hourKey][metric] = 0;
          }
          groupedData[hourKey][metric] = (groupedData[hourKey][metric] as number) + value;
        }
      });
    });

    let aggregatedData = Object.values(groupedData);

    const groupDataByInterval = (interval: number) => {
      const groupedData: { [key: string]: ChartData } = {};

      aggregatedData.forEach((entry: ChartData) => {
        const dt = DateTime.fromISO(entry.time, { setZone: true });  
      
        const timeKey =
        !["day", "week", "month"].includes(selectedFrequency) && entry.time.includes("T")
          ? dt.toFormat("MM/dd/yyyy hh:mm a")
          : dt.toFormat("MM/dd/yyyy");  
      
      
        if (!groupedData[timeKey]) {
          groupedData[timeKey] = { hourEnding: timeKey, time: entry.time };
        }
      
        // sum metrics
        const validMetrics = effectiveMetrics.filter((metric) =>
          Object.keys(entry).includes(metric)
        );

        validMetrics.forEach((metric: string) => {
          const metricValue = entry[metric] ?? 0;
          if (typeof metricValue === "number" && !isNaN(metricValue)) {
            if (groupedData[timeKey][metric] === undefined) {
              groupedData[timeKey][metric] = 0;
            }
            groupedData[timeKey][metric] =
              (groupedData[timeKey][metric] as number) + metricValue;
          }
        });
      });

      return Object.values(groupedData).sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
      );
    };

    if (useIntervalAggregation) {
      const minuteIntervals: { [key: string]: number } = {
        hour: 60,
        "15minute": 15,
        "5minute": 5,
      };

      const longIntervals: { [key: string]: number } = {
        day: 1440,
        week: 10080,
        month: 43200,
      };

      const isCustomDateRange = /^\d{4}-\d{2}-\d{2}\s*-\s*\d{4}-\d{2}-\d{2}$/.test(
        selectedDateRange
      );

      if (
        ["hour", "15minute", "5minute"].includes(selectedFrequency) &&
        (["minus_7d", "yesterday", "mtd", "ytd", "minus_30d","today","day_ahead"].includes(selectedDateRange) ||
          isCustomDateRange)
      ) {
        aggregatedData = groupDataByInterval(minuteIntervals[selectedFrequency]);
      } else if (
        ["day", "week", "month"].includes(selectedFrequency) ||
        isCustomDateRange
      ) {
        aggregatedData = groupDataByInterval(longIntervals[selectedFrequency]);
      }
    }

    const finalChartData = aggregatedData.sort((a, b) =>
      ["5minute", "15minute", "hour"].includes(selectedFrequency)
        ? new Date(a.time).getTime() - new Date(b.time).getTime()
        : new Date(a.time).setHours(0, 0, 0, 0) -
          new Date(b.time).setHours(0, 0, 0, 0)
    );

    setTableData(finalChartData);
  }, [
    volumeAsset,
    selectedResource,
    selectedFrequency,
    selectedDateRange,
    isvolumeLoading,
    volumeMetrics,
    useIntervalAggregation,
  ]);

  const toggleRowSelection = (index: number) => {
    setSelectedRows((prevSelectedRows) => {
      const newSelectedRows = new Set(prevSelectedRows);
      if (newSelectedRows.has(index)) {
        newSelectedRows.delete(index);
      } else {
        newSelectedRows.add(index);
      }
      return newSelectedRows;
    });
  };
const formatTableValue = (value: any) => {
  // if (value === null || value === undefined) return "-";
  if (value === null || value === undefined) {
  return <span className="text-gray-400">-</span>;
}

  const num = Number(value);

  if (Number.isNaN(num)) return value;

  // convert 0 / 0.0 / -0 to dash
  // if (Object.is(num, -0) || num === 0) return "-";
  if (Object.is(num, -0) || num === 0) {
  return <span className="text-gray-400">-</span>;
}

  // negatives in financial format
  // if (num < 0) {
  //   return `(${Math.abs(num).toLocaleString(undefined, {
  //     maximumFractionDigits: 2,
  //   })})`;
  // }

  return num.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
};
  const formatDateTime = (isoString: string, selectedFrequency: string) => {
     
      const dt = DateTime.fromISO(isoString, { setZone: true });
    
      const isDateOnly = ["day", "week", "month"].includes(selectedFrequency) || !isoString.includes("T");

      return isDateOnly
        ? dt.toFormat("MM/dd/yyyy  ")
        : dt.toFormat("MM/dd/yyyy hh:mm a ");
    };
const visibleMetricList = Object.values(allVolumeMetrics)
  .flat()
  .filter((metric) => visibility[metric]);

const filteredExportData = tableData.map((row) => {
  const formattedDate = useIntervalAggregation
    ? (row as any).hourEnding
    : formatDateTime(row.time, selectedFrequency);

  const filteredRow: Record<string, any> = {
    [useIntervalAggregation ? "Time" : "Date"]: formattedDate,
  };

  visibleMetricList.forEach((metric) => {
    filteredRow[metric] = row[metric];
  });

  return filteredRow;
});
const groupedHeaders = Object.entries(allVolumeMetrics)
  .flatMap(([groupKey, metrics]) =>
    metrics.filter((metric) => visibility[metric]).map(() => groupKey)
  );

const metricHeaders = Object.values(allVolumeMetrics)
  .flat()
  .filter((metric) => visibility[metric]);
  return (
    <div className="font-poppins">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-2">
        <h1 className="text-sm md:text-xl font-bold text-gray-600">{title}</h1>
        <div className="flex space-x-4">
          <FaRegFilePdf
            className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
            title="Export as PDF"
            onClick={() =>
              exportTableToPDF(
                "table-container",
                "VolumeTabReport",
                "VOLUME DATA",
                volumeData,
                logo
              )
            }
          />
          <BsFiletypeCsv
            className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
            title="Export as CSV"
            // onClick={() => exportToCSV(tableData, "exported_data")}
            onClick={() => exportToCSV(filteredExportData, "exported_data")}
          />
          <TbFileExcel
            className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
            title="Export as Excel"
            onClick={() =>
 
              exportTableToExcel(
                "VolumeTabReport",
                "VOLUME DATA",
                volumeData,
                logo,
                groupedHeaders,
                metricHeaders,
                filteredExportData
              )
            }
          />
          <FaRegCopy
            className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
            title="Copy Data"
            onClick={() => {
              const headers = [
                ["5minute", "15minute", "hour"].includes(selectedFrequency)
                  ? "Time"
                  : "Date",
                ...Object.values(allVolumeMetrics).flat().filter((m) => visibility[m]),
              ].join("\t");

              const formattedData = tableData
                .map((row) => {
                  const formattedTime = formatDateTime(row.time, selectedFrequency);
                  const values = Object.values(allVolumeMetrics)
                    .flat()
                    .filter((m) => visibility[m])
                    .map((m) => row[m] ?? "-")
                    .join("\t");
                  return `${formattedTime}\t${values}`;
                })
                .join("\n");

              navigator.clipboard.writeText(`${headers}\n${formattedData}`);
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto font-poppins min-h-[50vh] sm:min-h-[60vh] max-h-[75vh] sm:max-h-[110vh] lg:max-h-[65vh]">
        <div id="table-container" className="w-full min-h-[65vh] relative">
          {isvolumeLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
              <WindmillLoading />
            </div>
          )}
          <ApiErrorMessage ApiError={apiError} />
          <table className="min-w-full border-collapse border border-gray-300">
            <thead className="bg-indigo-300 hover:bg-indigo-400 transition-all duration-300 ease-in-out sticky top-0 z-10 truncate">
              <tr className="bg-[#E7E4FF] text-sm text-gray-600">
                <th className="border border-gray-300 px-4 py-2" rowSpan={2}>
                  {["5minute", "15minute", "hour"].includes(selectedFrequency)
                    ? "Time"
                    : "Date"}
                </th>
                {Object.entries(allVolumeMetrics).map(
                  ([groupKey, metrics]) =>
                    metrics.filter((m) => visibility[m]).length > 0 && (
                      <th
                        key={groupKey}
                        className="border border-gray-300 px-4 py-2 text-center"
                        colSpan={metrics.filter((m) => visibility[m]).length}
                      >
                        {groupKey}
                      </th>
                    )
                )}
              </tr>
              <tr className="bg-[#E7E4FF] text-sm text-gray-600">
                {Object.values(allVolumeMetrics).flat().map(
                  (metric) =>
                    visibility[metric] && (
                      <th
                        key={metric}
                        className="border border-gray-300 px-4 py-2 text-md font-normal"
                      >
                        {metric}
                      </th>
    )
)}
              </tr>
            </thead>
            <tbody>
  {tableData.map((row, index) => (
    <tr
      key={index}
      className={`${
        selectedRows.has(index)
          ? "bg-blue-200"
          : index % 2 === 0
          ? "bg-white"
          : "bg-[#F9F9F9]"
      }`}
      onClick={() => toggleRowSelection(index)}
    >
      <td className="border border-gray-300 px-4 py-2 truncate">
  {useIntervalAggregation
    ? (row as any).hourEnding
    : formatDateTime(row.time, selectedFrequency)}
</td>
      {/* <td className="border border-gray-300 px-4 py-2 truncate text-sm font-normal text-gray-600">
        {useIntervalAggregation
          ? (row as any).hourEnding
          : formatDateTime(row.time, selectedFrequency)}
      </td> */}
      

      {Object.values(allVolumeMetrics).flat().map(
        (metric) =>
          visibility[metric] && (
            <td
              key={metric}
              className="border border-gray-300 px-4 py-2 text-right"
            >
              {formatTableValue(row[metric])}
            </td>
          )
      )}
    </tr>
  ))}
</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TableComponent;
